/** Character definitions and the sprite/portrait bitmaps generated from them at boot. */

import { tr } from "../i18n";

/** 16×16 portrait grids. Letters index into a palette; "." is transparent. */
const PGRID = {
  clerk: [
    "................",
    ".....kkkkkk.....",
    "....khhhhhhk....",
    "...khhHhhhhhk...",
    "...khhhhhhhhk...",
    "...khsshhsshk...",
    "...kssssssssk...",
    "...ksessssesk...",
    "...kSssssssSk...",
    "...ksssmmsssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkwwsswwkk...",
    "..kwwwwccwwwwk..",
    ".kwwwwwccwwwwwk.",
    ".kwwWwwccwwWwwk.",
  ],
  vs: [
    "................",
    ".....kkkkkk.....",
    "....khhhhhhk....",
    "...khhhhhhhhk...",
    "...khHhhhhHhk...",
    "...khsssssshk...",
    "...ksbbssbbsk...",
    "...kgegssgegk...",
    "...kSssssssSk...",
    "...kssmmmmssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkwwsswwkk...",
    "..kwwwwrrwwwwk..",
    ".kwwwwwrrwwwwwk.",
    ".kwwWwwrrwwWwwk.",
  ],
  patient: [
    "....kkkkkkkk....",
    "...khhhhhhhhk...",
    "..khhHhhhhhhhk..",
    "..khhhhhhhhhhk..",
    "..khhhhhhhhhhk..",
    "..khhsshhsshhk..",
    "..khsssssssshk..",
    "..khsesssseshk..",
    "..khSssssssShk..",
    "..khsssmmssshk..",
    "..khhSssssShhk..",
    "..khhhkSSkhhhk..",
    "..khhccsscchhk..",
    ".khhcccccccchhk.",
    ".khcccCccCccchk.",
    ".kcccccCCccccck.",
  ],
  mother: [
    "....kkkkkkkk....",
    "...khhHhhHhhk...",
    "..khHhhhhhhHhk..",
    "..khhhhhhhhhhk..",
    "..khhhhhhhhhhk..",
    "..khhsssssshhk..",
    "..khsbbssbbshk..",
    "..khsesssseshk..",
    "..kkSssssssSkk..",
    "...ksssmmsssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkccwwcckk...",
    "..kccccwwcccck..",
    ".kcccccwwccccck.",
    ".kcCcccwwcccCck.",
  ],
  nurse: [
    "......kkkk......",
    ".....khhHhk.....",
    "....kkhhhhkk....",
    "...khhhhhhhhk...",
    "...khHhhhhhhk...",
    "...khsssssshk...",
    "...kssssssssk...",
    "...ksessssesk...",
    "...kSssssssSk...",
    "...kssmmmmssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkccsscckk...",
    "..kccccsscccck..",
    ".kcCccccccccCck.",
    ".kcccccccccccck.",
  ],
  r2: [
    "......k..k......",
    ".....khkkhkk....",
    "....khhhhhhhk...",
    "...khhHhhhhhk...",
    "...khhhhhhhhk...",
    "...khsshsshhk...",
    "...kssssssssk...",
    "...ksessssesk...",
    "...ksSssssSsk...",
    "...ksssmmsssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkccsscckk...",
    "..kccccsscccck..",
    ".kcccccccccccck.",
    ".kcCccccccccCck.",
  ],
  oldman: [
    "................",
    ".....kkkkkk.....",
    "....kssssssk....",
    "...ksssSSsssk...",
    "...khsssssshk...",
    "...khSSssSShk...",
    "...ksbbssbbsk...",
    "...ksessssesk...",
    "...kSssSSssSk...",
    "...kssmmmmssk...",
    "....kSssssSk....",
    ".....kkSSkk.....",
    "...kkccsscckk...",
    "..kccccsscccck..",
    ".kcccccccccccck.",
    ".kcCccccccccCck.",
  ],
} satisfies Record<string, string[]>;
export type GridKey = keyof typeof PGRID;

/** Base 16×16 walking sprite. */
const CHIBI: readonly string[] = [
  "................",
  "....kkkkkkkk....",
  "...khhhhhhhhk...",
  "..khhHhhhhhhhk..",
  "..khhhhhhhhhhk..",
  "..khsshhhhsshk..",
  "..kssssssssssk..",
  "..kssessssessk..",
  "..kSssssssssSk..",
  "...kssssssssk...",
  "....kwwwwwwk....",
  "...kwwwccwwwk...",
  "..kswwwccwwwsk..",
  "...kwwwwwwwwk...",
  "...kppk..kppk...",
  "...kook..kook...",
];

/** Row overrides layered on top of CHIBI. */
const OV = {
  walk: { 14: "....kppkkppk....", 15: "....kookkook...." },
  back: {
    5: "..khhhhhhhhhhk..",
    6: "..khhhhhhhhhhk..",
    7: "..khhhhhhhhhhk..",
    8: "..khhhhhhhhhhk..",
    9: "...khhhhhhhhk...",
  },
  bun: { 0: "......kkkk......", 1: ".....khhhhk.....", 2: "...kkhhhhhhkk..." },
  glass: { 7: "..ksgegssgegsk.." },
  long: { 9: "..hk" + "s".repeat(8) + "kh..", 10: "..hhkwwwwwwkhh.." },
  mask: { 8: "..kMMMMMMMMMMk..", 9: "...kMMMMMMMMk..." },
} satisfies Record<string, Record<number, string>>;
export type Overlay = keyof typeof OV;

export type Palette = Record<string, string>;

const BASEPAL: Palette = {
  k: "#1b1426",
  e: "#1b1426",
  m: "#b44d5a",
  w: "#f3f3f5",
  W: "#c4c7d6",
  g: "#2c2c3e",
  r: "#a8323a",
  b: "#4a3a3a",
  o: "#2a2230",
  p: "#3d4a6b",
  M: "#d9ecf4",
};

/** Surgical cap + gown recolour. */
const ORCAP = (cap: string, top: string): Palette => ({ h: cap, H: cap, w: top, W: top, c: top, C: top, p: top });

export interface CastDef {
  name: string;
  role?: string;
  grid?: GridKey;
  pal?: Palette;
  chibi?: Overlay[];
  /** Variants (OR attire) reuse this character's portrait instead of drawing their own. */
  portraitOf?: string;
}

export const CAST: Record<string, CastDef> = {
  clerk: {
    name: tr("你（Clerk）", "You (clerk)"),
    grid: "clerk",
    pal: { s: "#f1c8a0", S: "#d79a72", h: "#2b2230", H: "#4a3f5c", c: "#4f86c6" },
    chibi: [],
  },
  clerk_or: {
    name: tr("你（Clerk）", "You (clerk)"),
    grid: "clerk",
    pal: { s: "#f1c8a0", S: "#d79a72", ...ORCAP("#5f9dc8", "#7fb3d5") },
    chibi: ["mask"],
    portraitOf: "clerk",
  },
  clerk_gown: {
    name: tr("你（Clerk）", "You (clerk)"),
    grid: "clerk",
    pal: { s: "#f1c8a0", S: "#d79a72", ...ORCAP("#5f9dc8", "#2f5f9e") },
    chibi: ["mask"],
    portraitOf: "clerk",
  },
  vs: {
    name: tr("林主任", "Dr. Lin"),
    grid: "vs",
    pal: { s: "#ecc19a", S: "#c98f6a", h: "#b9b9c4", H: "#e6e6ee", b: "#6a6a78", m: "#8d4048", c: "#a8323a" },
    role: tr("婦產科主治", "OB/GYN attending"),
    chibi: ["glass"],
  },
  vs_or: {
    name: tr("林主任", "Dr. Lin"),
    grid: "vs",
    pal: { s: "#ecc19a", S: "#c98f6a", b: "#6a6a78", ...ORCAP("#3f7f6a", "#2f5f9e") },
    chibi: ["glass", "mask"],
    portraitOf: "vs",
  },
  r2: {
    name: tr("小周（R2）", "Chou (R2)"),
    grid: "r2",
    pal: {
      s: "#e9bd96",
      S: "#c88e66",
      h: "#2f2a26",
      H: "#55493f",
      c: "#4fae8c",
      C: "#3a8a6c",
      w: "#4fae8c",
      p: "#3a8a6c",
    },
    role: tr("婦產科住院醫師", "OB/GYN resident"),
    chibi: [],
  },
  r2_or: {
    name: tr("小周（R2）", "Chou (R2)"),
    grid: "r2",
    pal: { s: "#e9bd96", S: "#c88e66", ...ORCAP("#4fae8c", "#2f5f9e") },
    chibi: ["mask"],
    portraitOf: "r2",
  },
  yijun: {
    name: tr("怡君", "Yi-Chun"),
    grid: "patient",
    pal: {
      s: "#f4dccd",
      S: "#ddb5a2",
      h: "#3a2a22",
      H: "#5c4436",
      c: "#e8a0b8",
      C: "#c97f99",
      w: "#e8a0b8",
      W: "#c97f99",
      p: "#e8a0b8",
    },
    role: tr("30 歲，第一胎", "30, first pregnancy"),
    chibi: ["long"],
  },
  husband: {
    name: tr("志明", "Chih-Ming"),
    grid: "clerk",
    pal: {
      s: "#e9bd96",
      S: "#c88e66",
      h: "#1f1c24",
      H: "#3b3646",
      w: "#5e7fa6",
      W: "#4a6588",
      c: "#e8e8ec",
      p: "#3a3f52",
    },
    role: tr("怡君的先生", "Yi-Chun’s husband"),
    chibi: [],
  },
  meiling: {
    name: tr("美玲姐", "Mei-Ling"),
    grid: "nurse",
    pal: {
      s: "#eec49c",
      S: "#cf946c",
      h: "#3a2a2a",
      H: "#8a7a7a",
      c: "#b79ad8",
      C: "#9278b8",
      w: "#b79ad8",
      p: "#9278b8",
    },
    role: tr("產房資深護理師", "Senior labor & delivery nurse"),
    chibi: ["bun"],
  },
  xiaowen: {
    name: tr("小雯", "Hsiao-Wen"),
    grid: "nurse",
    pal: {
      s: "#f1c8a0",
      S: "#d49b74",
      h: "#2a2026",
      H: "#4a3a44",
      c: "#8fc1e8",
      C: "#6d9fc8",
      w: "#8fc1e8",
      p: "#6d9fc8",
    },
    role: tr("門診跟診護理師", "OB/GYN clinic nurse"),
    chibi: ["bun"],
  },
  ama: {
    name: tr("阿嬤", "Grandma"),
    grid: "mother",
    pal: {
      s: "#e2b48c",
      S: "#bf8a62",
      h: "#c9c9cf",
      H: "#ededf2",
      b: "#8a8a90",
      c: "#6d8a5a",
      C: "#55703f",
      w: "#6d8a5a",
      p: "#4a4a52",
    },
    role: tr("候診的阿嬤", "A grandma in the waiting room"),
    chibi: [],
  },
  xiaoting: {
    name: tr("曉婷", "Hsiao-Ting"),
    grid: "patient",
    pal: { s: "#f6dcc8", S: "#e2b9a4", h: "#1d1a26", H: "#3b3552", c: "#a9c8e8", C: "#86a8cc" },
    role: tr("19 歲，第二關的病人", "19, the patient from Level 2"),
    chibi: [],
  },
  mother: {
    name: tr("陳媽媽", "Mrs. Chen"),
    grid: "mother",
    pal: {
      s: "#efc39b",
      S: "#cf946c",
      h: "#5b3a2a",
      H: "#83573f",
      b: "#4a2e22",
      c: "#7b5aa6",
      C: "#5c4282",
      w: "#7b5aa6",
      p: "#3a3f52",
    },
    role: tr("曉婷的母親", "Hsiao-Ting’s mother"),
    chibi: [],
  },
  anes: {
    name: tr("陳醫師", "Dr. Chen"),
    grid: "clerk",
    pal: { s: "#ecc19a", S: "#c98f6a", ...ORCAP("#8a74c2", "#6f8fbf") },
    role: tr("麻醉科主治", "Anesthesiology attending"),
    chibi: ["mask"],
  },
  scrub: {
    name: tr("阿珍", "Jen"),
    grid: "nurse",
    pal: { s: "#eec49c", S: "#cf946c", ...ORCAP("#4fae8c", "#2f5f9e") },
    role: tr("刷手護理師", "Scrub nurse"),
    chibi: ["bun", "mask"],
  },
  circ: {
    name: tr("佩佩", "Pei-Pei"),
    grid: "nurse",
    pal: { s: "#f1c8a0", S: "#d49b74", ...ORCAP("#e89ab8", "#5aa9a0") },
    role: tr("流動護理師", "Circulating nurse"),
    chibi: ["bun", "mask"],
  },
  yizhen: {
    name: tr("宜蓁", "Yi-Zhen"),
    grid: "patient",
    pal: {
      s: "#f1cfb8",
      S: "#d9b39c",
      h: "#2b1d18",
      H: "#4a3428",
      c: "#9fc4a8",
      C: "#7ea88a",
      w: "#9fc4a8",
      W: "#7ea88a",
      p: "#9fc4a8",
    },
    role: tr("32 歲，第一胎 38 週", "32, first pregnancy, 38 weeks"),
    chibi: ["long"],
  },
  popo: {
    name: tr("婆婆", "Mother-in-law"),
    grid: "mother",
    pal: {
      s: "#e2b48c",
      S: "#bf8a62",
      h: "#2e2a36",
      H: "#5a5468",
      b: "#2a2a30",
      c: "#c0504a",
      C: "#963a36",
      w: "#c0504a",
      W: "#963a36",
      p: "#3a3f52",
    },
    role: tr("宜蓁的婆婆", "Yi-Zhen’s mother-in-law"),
    chibi: [],
  },
  afen: {
    name: tr("阿芬", "Fen"),
    grid: "nurse",
    pal: {
      s: "#eec49c",
      S: "#cf946c",
      h: "#2a2026",
      H: "#4a3a44",
      c: "#4f7fb8",
      C: "#3a5f8a",
      w: "#4f7fb8",
      W: "#3a5f8a",
      p: "#3a5f8a",
    },
    role: tr("急診護理師", "ER nurse"),
    chibi: ["bun"],
  },
  xiaoting_er: {
    name: tr("曉婷", "Hsiao-Ting"),
    grid: "patient",
    pal: {
      s: "#f6dcc8",
      S: "#e2b9a4",
      h: "#1d1a26",
      H: "#3b3552",
      c: "#d79bb0",
      C: "#b77892",
      w: "#d79bb0",
      W: "#b77892",
      p: "#3a3f52",
    },
    role: tr("19 歲，大一", "19, college freshman"),
    chibi: ["long"],
  },
  /* ---------- 一般外科 ---------- */

  gs_vs: {
    name: tr("江主任", "Dr. Chiang"),
    grid: "vs",
    pal: { s: "#e6b98f", S: "#c48a62", h: "#22202a", H: "#8a8a96", b: "#2a2630", r: "#2f5f9e", c: "#2f5f9e" },
    role: tr("一般外科主治（肝膽胰）", "General surgery attending (HPB)"),
    chibi: [],
  },
  gs_vs_or: {
    name: tr("江主任", "Dr. Chiang"),
    grid: "vs",
    pal: { s: "#e6b98f", S: "#c48a62", b: "#2a2630", ...ORCAP("#c9a64a", "#2f5f9e") },
    chibi: ["mask"],
    portraitOf: "gs_vs",
  },
  gs_r: {
    name: tr("阿凱（R3）", "Kai (R3)"),
    grid: "r2",
    pal: {
      s: "#e2b28a",
      S: "#c08560",
      h: "#1a1820",
      H: "#34303e",
      c: "#4a6fa8",
      C: "#3a5a8a",
      w: "#4a6fa8",
      W: "#3a5a8a",
      p: "#3a5a8a",
    },
    role: tr("外科住院醫師", "Surgery resident"),
    chibi: [],
  },
  gs_r_or: {
    name: tr("阿凱（R3）", "Kai (R3)"),
    grid: "r2",
    pal: { s: "#e2b28a", S: "#c08560", ...ORCAP("#4a6fa8", "#2f5f9e") },
    chibi: ["mask"],
    portraitOf: "gs_r",
  },
  np: {
    name: tr("佩珊學姊", "Pei-Shan"),
    grid: "nurse",
    pal: {
      s: "#f0c9a4",
      S: "#d29a76",
      h: "#3a2620",
      H: "#5e4034",
      c: "#ecebf4",
      C: "#c9c6dc",
      w: "#ecebf4",
      W: "#c9c6dc",
      p: "#5a5f7a",
    },
    role: tr("外科專科護理師", "Surgical nurse practitioner"),
    chibi: ["long"],
  },
  gs_nurse: {
    name: tr("欣怡", "Hsin-Yi"),
    grid: "nurse",
    pal: {
      s: "#f3cfae",
      S: "#d6a07c",
      h: "#2a1e24",
      H: "#4a3640",
      c: "#f0a8b8",
      C: "#cc8698",
      w: "#f0a8b8",
      p: "#cc8698",
    },
    role: tr("外科病房護理師", "Surgical ward nurse"),
    chibi: ["bun"],
  },
  opd_nurse: {
    name: tr("淑惠姐", "Shu-Hui"),
    grid: "nurse",
    pal: {
      s: "#e8bd96",
      S: "#c98e68",
      h: "#2e2228",
      H: "#7a6a70",
      c: "#9ad0b8",
      C: "#78ae96",
      w: "#9ad0b8",
      p: "#78ae96",
    },
    role: tr("外科門診資深護理師", "Senior surgery clinic nurse"),
    chibi: ["bun"],
  },
  gi_r: {
    name: tr("志豪（消化內科 R）", "Chih-Hao (GI fellow)"),
    grid: "clerk",
    pal: { s: "#edc39c", S: "#cc916a", h: "#24202a", H: "#423c4e", c: "#6aa86a", w: "#f3f3f5" },
    role: tr("消化內科住院醫師", "Gastroenterology fellow"),
    chibi: ["glass"],
  },
  ir: {
    name: tr("宗翰（放射科 R）", "Tsung-Han (IR)"),
    grid: "clerk",
    pal: { s: "#e9bf98", S: "#c98e68", h: "#1e1c26", H: "#3a3648", c: "#b88a4a", w: "#f3f3f5" },
    role: tr("介入放射科住院醫師", "Interventional radiology resident"),
    chibi: [],
  },
  chen: {
    name: tr("陳伯伯", "Mr. Chen"),
    grid: "oldman",
    pal: {
      s: "#e2b48c",
      S: "#bf8a62",
      h: "#b8b8c0",
      H: "#d8d8de",
      b: "#9a9aa4",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    role: tr("66 歲，胃癌術後第 4 天", "66, post-op day 4, gastrectomy for gastric cancer"),
    chibi: [],
  },
  chen_dau: {
    name: tr("雅婷", "Ya-Ting"),
    grid: "patient",
    pal: {
      s: "#f2d2bc",
      S: "#dab09a",
      h: "#2a1c18",
      H: "#4a3428",
      c: "#e0b860",
      C: "#bf9640",
      w: "#e0b860",
      W: "#bf9640",
      p: "#3a3f52",
    },
    role: tr("陳伯伯的女兒", "Mr. Chen’s daughter"),
    chibi: ["long"],
  },
  zhiwei: {
    name: tr("志偉", "Chih-Wei"),
    grid: "clerk",
    pal: {
      s: "#efc6a0",
      S: "#d0956e",
      h: "#2a2228",
      H: "#4a3e48",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    role: tr("34 歲，疝氣術後第 1 天", "34, post-op day 1, hernia repair"),
    chibi: [],
  },
  tsai: {
    name: tr("蔡先生", "Mr. Tsai"),
    grid: "oldman",
    pal: {
      s: "#d9ac84",
      S: "#b8825c",
      h: "#3a3038",
      H: "#5a4e58",
      b: "#2a2228",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    role: tr("58 歲，肝切除術後第 2 天", "58, post-op day 2, liver resection"),
    chibi: [],
  },
  xiuqin: {
    name: tr("秀琴阿嬤", "Grandma Hsiu-Chin"),
    grid: "mother",
    pal: {
      s: "#e0b088",
      S: "#bc865e",
      h: "#d8d8de",
      H: "#f2f2f6",
      b: "#a8a8b0",
      c: "#8a6ab0",
      C: "#6a4e90",
      w: "#8a6ab0",
      W: "#6a4e90",
      p: "#4a4a52",
    },
    role: tr("78 歲，吐了一整天", "78, vomiting all day"),
    chibi: [],
  },
  meihua: {
    name: tr("美華", "Mei-Hua"),
    grid: "mother",
    pal: {
      s: "#efc39b",
      S: "#cf946c",
      h: "#3a2820",
      H: "#5e4234",
      b: "#2e2018",
      c: "#4f8a8a",
      C: "#3a6a6a",
      w: "#4f8a8a",
      W: "#3a6a6a",
      p: "#3a3f52",
    },
    role: tr("秀琴阿嬤的女兒", "Grandma Hsiu-Chin’s daughter"),
    chibi: [],
  },
  huang: {
    name: tr("黃先生", "Mr. Huang"),
    grid: "oldman",
    pal: {
      s: "#dcb082",
      S: "#ba8458",
      h: "#2a2428",
      H: "#4a4048",
      b: "#2a2228",
      c: "#c8a050",
      C: "#a07e38",
      w: "#c8a050",
      W: "#a07e38",
      p: "#3a3f52",
    },
    role: tr("55 歲，計程車司機", "55, taxi driver"),
    chibi: [],
  },
  huang_gown: {
    name: tr("黃先生", "Mr. Huang"),
    grid: "oldman",
    pal: {
      s: "#dcb082",
      S: "#ba8458",
      h: "#2a2428",
      H: "#4a4048",
      b: "#2a2228",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    chibi: [],
    portraitOf: "huang",
  },
  huang_wife: {
    name: tr("黃太太", "Mrs. Huang"),
    grid: "mother",
    pal: {
      s: "#ecc09a",
      S: "#cc916a",
      h: "#4a2e28",
      H: "#6e4a40",
      b: "#3a2420",
      c: "#d07a6a",
      C: "#ae5e50",
      w: "#d07a6a",
      W: "#ae5e50",
      p: "#3a3f52",
    },
    role: tr("黃先生的太太", "Mr. Huang’s wife"),
    chibi: [],
  },
  wu: {
    name: tr("吳太太", "Mrs. Wu"),
    grid: "mother",
    pal: {
      s: "#e6bc98",
      S: "#c48c6c",
      h: "#3a3038",
      H: "#8a8090",
      b: "#4a4048",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    role: tr("62 歲，Whipple 術後第 9 天", "62, post-op day 9, Whipple"),
    chibi: [],
  },
  li: {
    name: tr("李伯伯", "Mr. Li"),
    grid: "oldman",
    pal: {
      s: "#dfb088",
      S: "#bc865e",
      h: "#c0c0c8",
      H: "#dcdce2",
      b: "#a0a0a8",
      c: "#a9c8e8",
      C: "#86a8cc",
      w: "#a9c8e8",
      W: "#86a8cc",
      p: "#86a8cc",
    },
    role: tr("71 歲，肝切除術後第 1 天", "71, post-op day 1, liver resection"),
    chibi: [],
  },
  zheng: {
    name: tr("鄭先生", "Mr. Cheng"),
    grid: "clerk",
    pal: {
      s: "#e6b88e",
      S: "#c68a62",
      h: "#18161e",
      H: "#302c38",
      c: "#2a2a34",
      w: "#5a5a6a",
      W: "#44444f",
      p: "#2a2a34",
    },
    role: tr("隔壁床的家屬", "Family member at the next bed"),
    chibi: [],
  },
  bank: { name: tr("血庫", "Blood bank") },
  sys: { name: "" },
  er: {
    name: tr("阿哲（急診 R）", "Che (ER resident)"),
    grid: "r2",
    pal: {
      s: "#eec49c",
      S: "#cd936a",
      h: "#22202c",
      H: "#3e3a50",
      c: "#3f6fd1",
      C: "#2f56a8",
      w: "#3f6fd1",
      W: "#2f56a8",
      p: "#2f56a8",
    },
    chibi: [],
    role: tr("急診住院醫師", "Emergency medicine resident"),
  },
};

export const castName = (k: string | null | undefined): string => (k ? (CAST[k]?.name ?? "") : "");

export interface Sprite {
  f0: HTMLCanvasElement;
  f1: HTMLCanvasElement;
  b0: HTMLCanvasElement;
  b1: HTMLCanvasElement;
}

export const PORTRAIT: Record<string, string> = {};
export const SPRITE: Record<string, Sprite> = {};

function drawGrid(rows: readonly string[], pal: Palette): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = 16;
  cv.height = 16;
  const x = cv.getContext("2d");
  if (!x) throw new Error("2d context unavailable");
  rows.forEach((row, y) => {
    for (let i = 0; i < row.length; i++) {
      const ch = row.charAt(i);
      const col = pal[ch];
      if (ch !== "." && col) {
        x.fillStyle = col;
        x.fillRect(i, y, 1, 1);
      }
    }
  });
  return cv;
}

function chibiRows(list: readonly Overlay[]): string[] {
  const r = CHIBI.slice();
  for (const n of list) for (const [k, v] of Object.entries(OV[n])) r[Number(k)] = v;
  return r;
}

function fullPalette(c: CastDef, over: Palette = {}): Palette {
  const merged: Palette = { ...BASEPAL, ...c.pal, ...over };
  // "C" (cloth shade) falls back to "c" (cloth).
  return { C: merged.c ?? "", ...merged };
}

/** Render every portrait and sprite once; call at boot before anything draws. */
export function makeArt(): void {
  for (const [k, c] of Object.entries(CAST)) {
    if (!c.grid) continue;
    const pal = fullPalette(c);
    if (!c.portraitOf) PORTRAIT[k] = drawGrid(PGRID[c.grid], pal).toDataURL();
    if (c.chibi) {
      const front = c.chibi;
      const o = front.filter((n) => n !== "long");
      const back: Overlay[] = ["back", ...o.filter((n) => n !== "glass" && n !== "mask")];
      SPRITE[k] = {
        f0: drawGrid(chibiRows(front), pal),
        f1: drawGrid(chibiRows([...front, "walk"]), pal),
        b0: drawGrid(chibiRows(back), pal),
        b1: drawGrid(chibiRows([...back, "walk"]), pal),
      };
    }
  }
  for (const [k, c] of Object.entries(CAST)) {
    const p = c.portraitOf && PORTRAIT[c.portraitOf];
    if (p) PORTRAIT[k] = p;
  }
}

/** Portrait <img> for the side panel, toasts and title cards. */
export function pimg(k: string): HTMLImageElement {
  const i = document.createElement("img");
  i.className = "px";
  i.src = PORTRAIT[k] ?? "";
  i.alt = castName(k);
  return i;
}

const PORTRAIT_CV = new Map<string, HTMLCanvasElement>();

/** Portrait as a canvas (for close-ups), optionally recoloured (e.g. pale skin when in shock). */
export function portraitCanvas(k: string, palOver: Palette = {}): HTMLCanvasElement {
  const id = k + JSON.stringify(palOver);
  const hit = PORTRAIT_CV.get(id);
  if (hit) return hit;
  const c = CAST[k];
  if (!c?.grid) throw new Error(`no portrait grid for ${k}`);
  const cv = drawGrid(PGRID[c.grid], fullPalette(c, palOver));
  PORTRAIT_CV.set(id, cv);
  return cv;
}

/** Sprite lookup that fails loudly instead of drawing nothing. */
export function sprite(k: string): Sprite {
  const s = SPRITE[k];
  if (!s) throw new Error(`no sprite for ${k}`);
  return s;
}
